<template>
  <v-card flat>
    <v-row no-gutters class="py-0 px-2">
      <v-col cols="12" class="px-0">
        <a type="primary" size="xsmall" @click="beforeLeave">
          <v-icon size="12" class="backIcon">mdi-arrow-left</v-icon>
          {{ $t('message.actions.return') }}
        </a>
      </v-col>
      <v-col cols="12">
        <span
          class="body-bold-medium page__bigPlayBtn--title"
          style="vertical-align: middle"
        >
          {{ $t('message.pages.video.controls') }}
        </span>
      </v-col>
    </v-row>

    <v-row no-gutters class="py-0 px-2">
      <v-col cols="12">
        <v-card flat>
          <span class="body-regular-smaller page__bigPlayBtn--subtext">
            {{ $t('message.pages.video.messageControls') }}
            <span
              class="cursor-pointer body-bold-smaller page__bigPlayBtn--article"
              @click="openHelpDesk"
              >{{ $t('message.actions.knowMore') }}
              <v-icon size="11px" class="page__bigPlayBtn--article-icon"
                >mdi-open-in-new</v-icon
              ></span
            >
          </span>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="px-2 my-3">
      <v-col cols="12">
        <BaseAlert
          type="informative"
          :title="$t('message.pages.video.titleAlertBigPlayButton')"
          :description="$t('message.pages.video.textAlertPlayButton')"
        />
      </v-col>
    </v-row>

    <div>
      <h3 class="body-bold-base pl-2 pb-2">
        {{ $t('message.playerPage.controls') }}
      </h3>

      <BaseActionList
        v-for="(item, index) in controlsItems"
        :title="item.label"
        :key="`control-item-${index}`"
        :iconName="!!item.icon ? '' : 'mdi-help'"
        class="base-action-list"
      >
        <template #custom-icon v-if="item.icon">
          <simple-svg :src="item.icon" width="16px" />
        </template>

        <template #secondary-action>
          <v-tooltip v-if="item.value === 'play'" top>
            <template v-slot:activator="{ on }">
              <div v-on="on" class="d-inline-block">
                <v-switch
                  v-model="localConfig.controls"
                  class="mt-0"
                  :value="item.value"
                  @click="changePlayer(false, true)"
                  :disabled="localConfig.disablePause || !hasEditVideoPerm"
                  hide-details
                />
              </div>
            </template>
            <div class="d-flex flex-column align-center">
              <span
                >{{ $t('message.pages.video.disabledDisablePauseText1') }}
              </span>
              <span
                >{{ $t('message.pages.video.disabledDisablePauseText2') }}
              </span>
              <span>{{
                $t('message.pages.video.disabledDisablePauseText3')
              }}</span>
            </div>
          </v-tooltip>

          <v-switch
            v-else
            v-model="localConfig.controls"
            class="mt-0"
            :value="item.value"
            @click="changePlayer(false, true)"
            :disabled="!hasEditVideoPerm"
            hide-details
          />
        </template>
      </BaseActionList>
    </div>

    <v-divider class="my-6 mx-2"></v-divider>

    <div>
      <h3 class="body-bold-base pl-2 pb-2">
        {{ $t('message.pages.video.controlsPage.resources') }}
      </h3>

      <BaseActionList
        v-for="(item, index) in resourcesItems"
        :title="item.label"
        :subtitle="item.description"
        :key="`control-item-${index}`"
        class="base-action-list"
      >
        <template #secondary-action>
          <v-switch
            v-if="item.value === 'hideControlsOnStart'"
            v-model="localConfig.hideControlsOnStart"
            class="mt-0"
            @click="changePlayer(false, true)"
            hide-details
            :disabled="!hasEditVideoPerm"
          />
          <v-switch
            v-else-if="item.value === 'restartAfterEnd'"
            v-model="localConfig.restartAfterEnd"
            class="mt-0"
            @click="changePlayer(false, true)"
            hide-details
            :disabled="!hasEditVideoPerm"
          />
          <v-switch
            v-else-if="item.value === 'disablePause'"
            v-model="localConfig.disablePause"
            class="mt-0"
            @click="changePlayer(false, true)"
            hide-details
            :disabled="!hasEditVideoPerm"
          />
          <v-switch
            v-else-if="item.value === 'disableForward'"
            v-model="localConfig.disableForward"
            class="mt-0"
            @click="changePlayer(false, true)"
            hide-details
            :disabled="!hasEditVideoPerm"
          />
          <v-tooltip
            :disabled="!alternativeProgress"
            v-else-if="item.value === 'progress'"
            top
          >
            <template v-slot:activator="{ on }">
              <div v-on="on" class="d-inline-block">
                <v-switch
                  v-model="localConfig.controls"
                  class="mt-0"
                  :value="item.value"
                  @click="changePlayer(false, true)"
                  :disabled="alternativeProgress || !hasEditVideoPerm"
                  hide-details
                />
              </div>
            </template>
            <span>{{
              $t('message.pages.video.disabledAlternativeProgress')
            }}</span>
          </v-tooltip>
          <v-switch
            v-else
            v-model="localConfig.controls"
            class="mt-0"
            :value="item.value"
            @click="changePlayer(false, true)"
            hide-details
            :disabled="!hasEditVideoPerm"
          />
        </template>
      </BaseActionList>
    </div>

    <BaseActionList :title="$t('message.playerPage.language')">
      <template #secondary-action>
        <v-select
          v-model="localConfig.defaultLanguage"
          :items="languages"
          :menu-props="{ top: false, offsetY: true }"
          item-text="name"
          item-value="value"
          outlined
          dense
          hide-details
          @change="changePlayer"
          :disabled="!hasEditVideoPerm"
        />
      </template>
    </BaseActionList>

    <unsaved-changes-modal />
    <portal to="actions-footer">
      <ActionModal
        :showAction1="hasEditVideoPerm"
        :showAction2="hasEditVideoPerm"
        :disableTooltip1="!withoutChanges"
        :disableTooltip2="!withoutChanges"
        :disabledAction1="withoutChanges"
        :disabledAction2="withoutChanges"
        :handleAction1="handleUndoChanges"
        :handleAction2="handleUpdateControls"
        :loadingAction2="loadingButtonSave"
      />
    </portal>
  </v-card>
</template>

<script>
// external imports
import { mapActions, mapMutations, mapState, mapGetters } from 'vuex'
import _ from 'lodash'
import BaseAlert from '@/components/BaseAlert.vue'

// internal imports
import i18n from '@/translations/lang'
import linkHelper from '@/utils/linkHelper.mixin.js'

// API

// Components
import UnsavedChangesModal from '@/components/UnsavedChangesModal.vue'
import BaseActionList from '@/components/BaseActionList'
import profileApi from '@/services/profileApi'
import ActionModal from '@/components/ActionModal.vue'
export default {
  name: 'VideoPlayerControls',
  components: {
    UnsavedChangesModal,
    BaseAlert,
    BaseActionList,
    ActionModal,
  },
  mixins: [linkHelper],
  props: {
    handleBack: {
      type: Function,
      required: true,
    },
    liveVideoId: {
      required: false,
    },
    hasEdit: {
      type: Array,
    },
  },
  data: () => ({
    videoId: '',
    profileId: null,
    profileConfig: null,
    loadingButtonSave: false,
    videoPlayer: {},
    alternativeProgress: false,
    initialConfig: {
      controls: [],
      hideControlsOnStart: false,
      restartAfterEnd: false,
      disablePause: false,
      disableForward: false,
      defaultLanguage: '',
    },
    icons: {
      playLarge: require('@/assets/playerTabs/controls/icons/play-large.svg'),
      play: require('@/assets/playerTabs/controls/icons/play.svg'),
      forward: require('@/assets/playerTabs/controls/icons/fast-forward.svg'),
      rewind: require('@/assets/playerTabs/controls/icons/rewind.svg'),
      volume: require('@/assets/playerTabs/controls/icons/volume.svg'),
      captions: require('@/assets/playerTabs/controls/icons/captions.svg'),
      pip: require('@/assets/playerTabs/controls/icons/pip.svg'),
      settings: require('@/assets/playerTabs/controls/icons/settings.svg'),
      smartSearch: require('@/assets/playerTabs/controls/icons/smart-search.svg'),
      airplay: require('@/assets/playerTabs/controls/icons/airplay.svg'),
      cast: require('@/assets/playerTabs/controls/icons/cast.svg'),
      fullscreen: require('@/assets/playerTabs/controls/icons/fullscreen.svg'),
    },
    localConfig: {
      controls: [],
      hideControlsOnStart: false,
      restartAfterEnd: false,
      disablePause: false,
      disableForward: false,
      defaultLanguage: '',
    },

    withoutChanges: true,
  }),
  async mounted() {
    this.videoId = this.liveVideoId ? this.liveVideoId : this.$route.params.id
    this.profileId = this.$store.state.video.video.config?.profile
    this.videoPlayer = this.$store.state.video.player.config
    this.alternativeProgress =
      this.$store.state.video.profile.vsl?.alternativeProgress
    this.localConfig.defaultLanguage =
      this.$store.state.video.profile.vsl?.defaultLanguage || ''
    this.localConfig.controls = this.$store.state.video.profile.controls
      ? this.$store.state.video.profile.controls
      : [
          'play-large',

          'rewind',

          'play',

          'fast-forward',

          'progress',

          'current-time',

          'volume',

          'captions',

          'settings',

          'pip',

          'fullscreen',
        ]
    this.localConfig.hideControlsOnStart =
      this.$store.state.video.profile?.hideControlsOnStart
    this.localConfig.restartAfterEnd =
      this.$store.state.video.profile?.vsl.restartAfterEnd
    this.localConfig.disableForward =
      this.$store.state.video.profile?.vsl.disableForward
    this.localConfig.disablePause =
      this.$store.state.video.profile?.vsl.disablePause

    this.initialConfig = _.cloneDeep(this.localConfig)
    this.hasEdit.push(this.checkChanges)
    this.$root.$on('discardChanges', () => {
      this.handleUndoChanges()
      this.handleBack()
    })
  },
  watch: {
    localConfig: {
      handler() {
        this.withoutChanges = this.checkChanges()
        if (this.localConfig.disablePause) {
          const playControlIndex = this.localConfig.controls.findIndex(
            (control) => control === 'play'
          )

          if (playControlIndex !== -1) {
            const newControls = [...this.localConfig.controls]
            newControls.splice(playControlIndex, 1)

            this.$set(this.localConfig, 'controls', newControls)
            this.localConfig.disablePause = true
            this.changePlayer(false, true)
          }
        }
      },
      deep: true,
    },
  },
  computed: {
    ...mapState({
      profile: (state) => state.video.profile,
      video: (state) => state.video.video,
      profile_id: (state) => state.video.profile_id,
      internalError: (state) => state.video.internalError,
    }),
    controlsItems() {
      this.$i18next.resolvedLanguage
      return [
        {
          label: 'Play/Pause',
          value: 'play',
          icon: this.icons.play,
        },
        {
          label: i18n.t('message.pages.video.controlsPage.forward'),
          value: 'fast-forward',
          icon: this.icons.forward,
        },
        {
          label: i18n.t('message.pages.video.controlsPage.backward'),
          value: 'rewind',
          icon: this.icons.rewind,
        },
        {
          label: 'Volume',
          value: 'volume',
          icon: this.icons.volume,
        },
        {
          label: i18n.t('message.pages.video.controlsPage.captions'),
          value: 'captions',
          icon: this.icons.captions,
        },
        {
          label: i18n.t('message.pages.video.controlsPage.pip'),
          value: 'pip',
          icon: this.icons.pip,
        },
        {
          label: i18n.t('message.pages.video.controlsPage.settings'),
          value: 'settings',
          icon: this.icons.settings,
        },
        // {
        //   label: i18n.t('message.pages.video.controlsPage.smartSearch'),
        //   value: 'smartSearch',
        //   icon: this.icons.smartSearch,
        // },
        {
          label: 'Airplay',
          value: 'airplay',
          icon: this.icons.airplay,
        },
        {
          label: 'Chromecast',
          value: 'cast',
          icon: this.icons.cast,
        },
        {
          label: 'Fullscreen',
          value: 'fullscreen',
          icon: this.icons.fullscreen,
        },
      ]
    },
    mustReloadToPreview() {
      return (
        !_.isEqual(
          this.profile.hideControlsOnStart,
          this.localConfig.hideControlsOnStart
        ) ||
        (!_.isEqual(
          this.profile.vsl.defaultLanguage,
          this.localConfig.defaultLanguage
        ) &&
          this.localConfig.defaultLanguage !== '') /*||
        this.localConfig.controls.includes('cast') !==
          this.initialConfig.controls.includes('cast') ||
        this.localConfig.controls.includes('airplay') !==
          this.initialConfig.controls.includes('airplay')*/
      )
    },
    ...mapGetters('user', ['hasEditVideoPerm']),
    languages() {
      return [
        {
          value: '',
          name: this.$i18next.t('message.automatic'),
        },
        {
          value: 'pt',
          name: this.$i18next.t('message.portuguese'),
        },
        {
          value: 'en',
          name: this.$i18next.t('message.english'),
        },
        {
          value: 'es',
          name: this.$i18next.t('message.spanish'),
        },
      ]
    },
    resourcesItems() {
      this.$i18next.resolvedLanguage
      return  [
        {
          label: i18n.t(
            'message.pages.video.controlsPage.currentTimeControlsPage.label'
          ),
          description: i18n.t(
            'message.pages.video.controlsPage.currentTimeControlsPage.description'
          ),
          value: 'current-time',
        },
        {
          label: i18n.t('message.pages.video.controlsPage.hideControls.label'),
          description: i18n.t(
            'message.pages.video.controlsPage.hideControls.description'
          ),
          value: 'hideControlsOnStart',
        },
        {
          label: i18n.t('message.pages.video.controlsPage.progress.label'),
          description: i18n.t(
            'message.pages.video.controlsPage.progress.description'
          ),
          value: 'progress',
        },
        {
          label: i18n.t('message.pages.video.controlsPage.restartVideo.label'),
          description: i18n.t(
            'message.pages.video.controlsPage.restartVideo.description'
          ),
          value: 'restartAfterEnd',
        },
        {
          label: i18n.t(
            'message.pages.video.controlsPage.desactivePause.label'
          ),
          description: i18n.t(
            'message.pages.video.controlsPage.desactivePause.description'
          ),
          value: 'disablePause',
        },
        {
          label: i18n.t(
            'message.pages.video.controlsPage.desactiveDoubleTouch.label'
          ),
          description: i18n.t(
            'message.pages.video.controlsPage.desactiveDoubleTouch.description'
          ),
          value: 'disableForward',
        },
      ]
    },
  },
  methods: {
    ...mapActions('video', [
      'updateProfile',

      'createProfile',

      'setProfileToVideos',
    ]),
    ...mapMutations('video', [
      'UPDATE_VIDEO_PROFILE',
      'UPDATE_VIDEO_PROFILE_ID',
      'UPDATE_STATUS_ERROR',
    ]),
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    openHelpDesk() {
      const link = this.getCustomizeControls()
      window.open(link, '_blank')
    },
    async handleUpdateControls() {
      this.loadingButtonSave = true
      this.organizeControls()
      const colors = this.$store.state.video.profile.colors
      let vsl = this.$store.state.video.profile.vsl

      vsl.disableForward = this.localConfig.disableForward
      vsl.disablePause = this.localConfig.disablePause
      vsl.restartAfterEnd = this.localConfig.restartAfterEnd

      try {
        if (this.video.config.profile || this.profile_id) {
          const config_profile = await profileApi.getProfileId(
            this.video.config.profile || this.profile_id
          )
          if (config_profile.data.pullzone_name !== this.video.pullzone_name) {
            await this.createProfile(this.videoId)
            this.video.config.profile = this.profile_id
            this.profileId = this.profile_id
            await this.setProfileToVideos([this.profileId, this.videoId])
          }
        }
        if (!this.video.config.profile) {
          await this.createProfile(this.videoId)
          this.video.config.profile = this.profile_id
          this.profileId = this.profile_id
          await this.setProfileToVideos([this.profileId, this.videoId])
        }
        await this.updateProfile([
          this.profileId,
          {
            controls: this.localConfig.controls,
            hideControlsOnStart: this.localConfig.hideControlsOnStart,
            vsl: vsl,
            colors: colors,
          },
        ])
        this.internalError
          ? ''
          : (this.handleSnackbar(this.$t('message.pages.video.changesSaved')),
            (this.initialConfig.controls = _.cloneDeep(
              this.localConfig.controls
            )),
            (this.initialConfig.hideControlsOnStart = _.cloneDeep(
              this.localConfig.hideControlsOnStart
            )),
            (this.initialConfig.restartAfterEnd = _.cloneDeep(
              this.localConfig.restartAfterEnd
            )),
            (this.initialConfig.disablePause = _.cloneDeep(
              this.localConfig.disablePause
            )),
            (this.initialConfig.disableForward = _.cloneDeep(
              this.localConfig.disableForward
            )),
            (this.initialConfig.defaultLanguage =
              this.localConfig.defaultLanguage),
            (this.withoutChanges = true))
        this.loadingButtonSave = false
      } catch (e) {
        console.error(e)
        this.loadingButtonSave = false
      }
      this.$root.$emit('updateEmbedPreviewChange')
    },
    /* eslint-disable-next-line */
    async changePlayer(forceReload = false, reloadPlayer = false) {
      reloadPlayer = true
      this.organizeControls()
      this.handlePreviewViaQueryParams()
      /*if (this.mustReloadToPreview || forceReload === true) {
        this.handlePreviewViaQueryParams()
      } else {
        this.handlePreviewViaPostMessage()
      }*/
      this.UPDATE_STATUS_ERROR(false)
      if (reloadPlayer) {
        this.$root.$emit('updateEmbedPreviewChange')
      }
    },
    handlePreviewViaQueryParams() {
      if (this.profile.vsl === null) this.profile.vsl = {}
      this.profile.hideControlsOnStart = this.localConfig.hideControlsOnStart
      this.profile.vsl.defaultLanguage = this.localConfig.defaultLanguage
      this.profile.controls = this.localConfig.controls
      this.profile.vsl.restartAfterEnd = this.localConfig.restartAfterEnd
      this.profile.vsl.disablePause = this.localConfig.disablePause
      this.profile.vsl.disableForward = this.localConfig.disableForward

      this.UPDATE_VIDEO_PROFILE(this.profile)
    },
    handlePreviewViaPostMessage() {
      const iframe = document.querySelector('iframe')
      iframe.contentWindow.postMessage(
        {
          type: 'bulk_update_player_customs',
          parameter: {
            controls: this.localConfig.controls,
            restartAfterEnd: this.localConfig.restartAfterEnd,
            disablePause: this.localConfig.disablePause,
            disableForward: this.localConfig.disableForward,
          },
        },
        '*'
      )
    },
    handleUndoChanges() {
      this.localConfig = _.cloneDeep(this.initialConfig)
      this.changePlayer(true)
    },
    checkChanges() {
      return _.isEqual(this.localConfig, this.initialConfig)
    },
    beforeLeave() {
      if (!this.checkChanges()) {
        this.$root.$emit('unsavedChangesModal')
      } else {
        this.UPDATE_STATUS_ERROR(false)
        this.handleBack()
      }
    },
    organizeControls() {
      const organizedControls = [
        'play-large',
        'rewind',
        'play',
        'fast-forward',
        'progress',
        'current-time',
        'volume',
        'captions',
        'settings',
        'pip',
        'fullscreen',
        'airplay',
        'cast',
      ]
      this.localConfig.controls.sort(function (a, b) {
        return organizedControls.indexOf(a) - organizedControls.indexOf(b)
      })
      return this.localConfig.controls
    },
    handleSnackbar(item) {
      if (!item) {
        item = i18n.t('message.changesSaved')
      }
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: item,
        type: 'success',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.player-subtitle {
  margin-bottom: 8px;
  font-weight: normal;

  + * {
    padding-top: 6px;
  }
}
.grid {
  display: grid;
  grid-template-areas: 'preview' 'control' 'spacer' 'footer';
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr;
  column-gap: 0;
  row-gap: 0;

  box-shadow: none !important;

  overflow: hidden;
  height: 100%;

  @media (min-width: 1200px) {
    grid-template-areas:
      'preview control'
      'preview spacer'
      'preview footer';
    grid-template-columns: 50% auto;
    grid-template-rows: auto 1fr auto;
  }

  &__preview {
    grid-area: preview;
    overflow: hidden;
    padding: $spacing-10;
    border-left: 1px solid varFigma(neutral-95-lightest) !important;

    &--container {
      margin: 0 auto;
      width: 75vw;

      @media (min-width: 760px) {
        width: 50vw;
      }

      @media (min-width: 1200px) {
        width: 100%;
      }
    }
  }

  &__control {
    grid-area: control;

    overflow-y: auto;
    overflow-x: hidden;

    height: 100%;
    padding: $spacing-10 $spacing-09 0 $spacing-09;
    margin-right: 2px;

    @media (min-width: 1200px) {
      border-left: 1px solid varFigma(neutral-95-lightest) !important;
    }
    @media (max-width: 1199px) {
      border-top: 1px solid varFigma(neutral-95-lightest) !important;
    }

    &--items {
      display: grid;
      padding-top: 0 !important;
    }
    &--item {
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 8px !important;
      &:hover {
        background-color: varFigma(neutral-99);
      }
      & .v-input::v-deep {
        padding: 0 !important;
        & .v-input--selection-controls__input {
          margin-right: 0 !important;
        }
      }
    }
  }
  &__resources {
    &--items {
      display: grid;
      padding-top: 0 !important;
    }
    &--item {
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 8px !important;
      &:hover {
        background-color: varFigma(neutral-99);
        &.affiliate {
          background-color: inherit;
        }
      }
      & .v-input::v-deep {
        padding: 0 !important;
        & .v-input--selection-controls__input {
          margin-right: 0 !important;
        }
      }
    }
    &--label {
      display: flex;
      flex-direction: column;
      align-items: start;
      .body-regular-smaller {
        color: varFigma(neutral-40-light);
      }
    }
  }
}
.page__bigPlayBtn {
  &--back,
  &--back-icon {
    color: varFigma(brand-60-base);
  }
  &--title,
  &--player-text {
    color: varFigma(neutral-20-dark);
  }
  &--article,
  &--article-icon {
    color: varFigma(neutral-30-base);
  }
  &--subtext,
  &--desc-feature {
    color: varFigma(neutral-40-light);
  }
}

.base-action-list {
  padding-right: 0px !important;
}
</style>
